import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/stores/authStore';

/**
 * Route guard for authenticated pages.
 *
 * - No token in localStorage  -> redirect to /login?redirect=<current path>
 * - Token present but user not yet fetched (e.g. full page reload) -> show a
 *   spinner until fetchUser resolves; if the token turns out to be stale,
 *   fetchUser clears auth state and we re-render into the login redirect.
 */
const RequireAuth: React.FC = () => {
    const location = useLocation();
    const token = useAuthStore((state) => state.token);
    const user = useAuthStore((state) => state.user);
    const fetchUser = useAuthStore((state) => state.fetchUser);

    // Token exists but user object is missing (fresh page load). App.tsx also
    // triggers fetchUser on mount; this covers client-side navigation where
    // the store was just reset (e.g. after logout + re-login on another tab).
    useEffect(() => {
        if (token && !user) {
            fetchUser();
        }
    }, [token, user, fetchUser]);

    if (!token) {
        const redirect = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/login?redirect=${redirect}`} replace />;
    }

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-base-200">
                <Spinner size="lg" />
            </div>
        );
    }

    return <Outlet />;
};

export default RequireAuth;
